import { DisplayObject, Matrix, Rectangle, Renderer, RenderTexture, Texture } from "pixi.js"

/**
 * Art rendered once into shared pages rather than a texture each. A batch can hold sixteen textures, and a castle frame
 * drew from 131 of them -- 87 of them composite tiles, each its own render texture -- so pixi flushed and rebound
 * texture slots over and over: in Chrome each GL call crosses to the GPU process (~14us), and the binds outnumbered the
 * draws. On a few pages, a storey's trees, bushes and props draw in a batch or two.
 *
 * Shelf-packed with a gap of transparent pixels round each piece, so a nearest-sampled edge never picks up a neighbour.
 * Pages are never freed (neither were the textures they replace): art outlives the room it was first drawn in.
 */
export class RenderTextureAtlas {
    private pages: { texture: RenderTexture; x: number; y: number; shelf: number }[] = []
    private readonly transform = new Matrix()

    constructor(private readonly size = 2048, private readonly gap = 2) {}

    /**
     * Renders `art` (its local area `region`) into a page and returns that piece of the page; undefined when it is
     * bigger than a page, for the caller to give it a texture of its own.
     */
    put(renderer: Pick<Renderer, "generateTexture"> & Partial<Renderer>, art: DisplayObject, region: Rectangle): Texture | undefined {
        const width = Math.ceil(region.width), height = Math.ceil(region.height)
        if (!width || !height || width + this.gap > this.size || height + this.gap > this.size) return undefined
        // A stand-in renderer (tests) that cannot draw into a page: the caller makes its own texture as before.
        if (typeof renderer?.render !== "function" || !renderer.gl) return undefined
        // A page someone destroyed (a texture freed with its base): its pieces are gone; start another.
        if (this.pages.length && this.pages[this.pages.length - 1].texture.baseTexture.destroyed) this.pages.pop()
        let page = this.pages[this.pages.length - 1]
        const fits = (p: typeof page) => {
            if (p.x + width + this.gap > this.size) {
                p.x = 0
                p.y += p.shelf
                p.shelf = 0
            }
            return p.y + height + this.gap <= this.size
        }
        // Never draw a page into itself: art sampling the page it would land on is a feedback loop on the GPU.
        if (page && samples(art, page.texture.baseTexture)) return undefined
        if (!page || !fits(page)) {
            page = { texture: RenderTexture.create({ width: this.size, height: this.size }), x: 0, y: 0, shelf: 0 }
            this.pages.push(page)
        }
        const x = page.x + this.gap, y = page.y + this.gap
        this.transform.set(1, 0, 0, 1, x - region.x, y - region.y)
        renderer.render!(art, { renderTexture: page.texture, clear: false, transform: this.transform })
        page.x += width + this.gap
        page.shelf = Math.max(page.shelf, height + this.gap)
        return new Texture(page.texture.baseTexture, new Rectangle(x, y, width, height))
    }

    get pageCount(): number {
        return this.pages.length
    }
}

/** Whether any sprite in `art` draws from `base`. */
const samples = (art: DisplayObject, base: unknown): boolean => {
    const node = art as DisplayObject & { texture?: Texture; children?: DisplayObject[] }
    if (node.texture?.baseTexture === base) return true
    return !!node.children?.some(child => samples(child, base))
}

/** The art baked from tilesets -- composite tiles -- in shared pages, so a storey's trees and props batch together. */
export const artAtlas = new RenderTextureAtlas()

/**
 * Art baked from that art -- a tree with its root fringe or soil -- on pages of its own: drawing an artAtlas tree into an
 * artAtlas page could land it on the page it is read from.
 */
export const bakedArtAtlas = new RenderTextureAtlas()
